import { queryChartData } from '@gonfalon/openapi/operations/queryChartData';
import { useQuery } from '@tanstack/react-query';

import { getLocalIanaTimeZone } from '#utils/dateUtils';

type TimeRange = { from: number; to: number; interval: string };

export async function fetchExperimentTrafficData(
  timeRange: TimeRange,
  projectKey: string,
  environmentKey: string,
  keys: string[],
) {
  const timezone = getLocalIanaTimeZone();
  const timeRangeForApi = {
    from: timeRange.from,
    to: timeRange.to,
    ...(timeRange.interval && { interval: timeRange.interval }),
    timezone,
  };
  const filterValues = [{ field: 'treatment_id', op: 'in' as const, values: keys }];

  const { data, error } = await queryChartData({
    apiVersion: '20240415',
    body: {
      projectKey,
      environmentKey,
      dataSource: {
        domain: 'experimentation',
        kind: 'audience',
        timeRange: timeRangeForApi,
      },
      select: [{ field: '', expression: 'cumulative_count' }],
      groupBy: [{ field: 'treatment_id' }],
      filters: { and: filterValues },
    },
  });

  if (error || !data) {
    throw new Error(
      error && typeof error === 'object' && 'message' in error ? String(error.message) : 'Failed to fetch chart data',
    );
  }

  return data;
}

export function useExperimentTrafficData(
  timeRange: TimeRange,
  projectKey: string,
  environmentKey: string,
  keys: string[],
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['experimentTrafficData', projectKey, environmentKey, timeRange, keys],
    queryFn: () => fetchExperimentTrafficData(timeRange, projectKey, environmentKey, keys),
    enabled: enabled && keys.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}
